import React, { useState, useEffect } from "react";
import { Card, Row, Col, Typography, Divider, Space, Button, Tooltip, Spin, message, Tag, Progress, Table } from "antd";
import DonutChart from "../../components/DonutChart";
import {
  DollarCircleOutlined,
  QuestionCircleOutlined,
  CheckCircleOutlined,
  BorderOutlined
} from "@ant-design/icons";
import _NewapplicationService from "../../modules/NewapplicationService";
import { useParams } from "react-router-dom";
import { PRIVATE_WEALTH_ADVISOR_ROLE, COMPLIANCE_ADMINISTRATOR_ROLE, UNDERWRITER_BANKER_ROLE } from "../../constants/DataConstants";
import { COMMON_ERROR_MSG } from "../../constants/constants";

import moment from "moment";
import _commonService from "../../modules/commonService";
import StatusTag from "../../components/StatusTag";

const { Title, Text } = Typography;

const LoanDetailsHeader = ({ applicantDetails }) => {
  // --- Helper Component for Field Data ---
  const DetailItem = ({ label, value }) => (
    <div className="lh-detail-item">
      <span className="lh-label">{label}</span>
      <div className="lh-value b-bottm">
        {value || "N/A"}
      </div>
    </div>
  );

  // --- Helpers for the Review Activity timeline (Review Comments modal) ---
  const getInitials = (name) => {
    if (!name) return "?";
    const parts = name.trim().split(/\s+/);
    return parts.length > 1
      ? (parts[0][0] + parts[1][0]).toUpperCase()
      : parts[0].slice(0, 2).toUpperCase();
  };

  const ROLE_TAG_COLORS = {
    "Compliance Administrator": { bg: "rgba(245, 158, 11, 0.15)", color: "#F59E0B" },
    "Underwriter Banker": { bg: "rgba(59, 130, 246, 0.15)", color: "#3B82F6" },
    "Senior Private Banker": { bg: "rgba(139, 92, 246, 0.15)", color: "#8B5CF6" },
    "Loan Officer": { bg: "rgba(20, 184, 166, 0.15)", color: "#14B8A6" },
  };
  const AVATAR_COLORS = ["#7C6A46", "#4B5563", "#6D5A8A", "#3F6B63", "#7A5449", "#4F6A7A"];

  const getRoleTagStyle = (roleName) =>
    ROLE_TAG_COLORS[roleName] || { bg: "rgba(148, 163, 184, 0.15)", color: "#94A3B8" };

  const ROLE_DISPLAY_NAMES = {
    "Compliance Administrator": "Compliance",
    "Underwriter Banker": "Underwriter",
    "Senior Private Banker": "Senior Banker",
  };
  const getRoleDisplayName = (roleName) => ROLE_DISPLAY_NAMES[roleName] || roleName;

  const getAvatarColor = (name) => {
    const str = name || "?";
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
  };

  const userRole = localStorage.getItem("userRole");
  const isSeniorPrivateBanker = userRole === "Senior Private Banker";
  const isLoanOfficer = userRole == "LoanOfficer";
  const isComplianceAdministrator = localStorage.getItem("userRole") === COMPLIANCE_ADMINISTRATOR_ROLE;
  const isUnderwriterBanker = localStorage.getItem("userRole") === UNDERWRITER_BANKER_ROLE;

  const [loading, setLoading] = useState(false);
  const params = useParams();
  const ApplicationObjId = params.Id;

  // Review chain visibility is role-based only (always shown to these roles); the CBR mismatch
  // banner and the red-flag indicator on the Compliance Admin's comment are gated separately
  // on applicantDetails?.IsCBRMismatch so they only appear when a mismatch actually exists.
  const showReviewComments =
    (isSeniorPrivateBanker || isComplianceAdministrator || isUnderwriterBanker || isLoanOfficer);
  const hasCbrMismatch = applicantDetails?.IsCBRMismatch === true;

  // Measures the left card's bottom edge against the review box's top edge so the
  // review box's height can be capped to match exactly, with its own scroll for overflow,
  // instead of guessing a fixed height via CSS (flex/stretch didn't track the dynamic content).
  const leftCardRef = React.useRef(null);
  const reviewBoxRef = React.useRef(null);
  const [reviewBoxMaxHeight, setReviewBoxMaxHeight] = useState(null);

  useEffect(() => {
    if (!showReviewComments) return undefined;

    // reviewBoxRef is the inner scrollable div; its parent ".card-section" box adds its own
    // 24px bottom padding outside of it, so that padding must be subtracted here too, or the
    // outer card's visible border ends up 24px past the left card's instead of flush with it.
    const CARD_BOTTOM_PADDING = 26;
    const BOTTOM_GAP = CARD_BOTTOM_PADDING;
    const updateReviewBoxHeight = () => {
      if (leftCardRef.current && reviewBoxRef.current) {
        const leftBottom = leftCardRef.current.getBoundingClientRect().bottom;
        const reviewTop = reviewBoxRef.current.getBoundingClientRect().top;
        const available = Math.round(leftBottom - reviewTop - BOTTOM_GAP);
        if (available > 0) {
          setReviewBoxMaxHeight(available);
        }
      }
    };

    updateReviewBoxHeight();
    window.addEventListener("resize", updateReviewBoxHeight);
    return () => window.removeEventListener("resize", updateReviewBoxHeight);
  }, [showReviewComments, applicantDetails]);
  // const [applicantDetails, setApplicantDetails] = useState([]);

  // useEffect(() => {
  //   getApplicantDetailsByID(ApplicationObjId);
  // }, []);

  // const getApplicantDetailsByID = async () => {
  //   setLoading(true);
  //   try {
  //     const res = await _NewapplicationService.getApplicantInformationByApplicationId(ApplicationObjId);
  //     if (res.data) {
  //       let resObj = res?.data;
  //       setApplicantDetails(resObj);
  //       setLoading(false);
  //     } else {
  //       message.error(COMMON_ERROR_MSG)
  //       setLoading(false);
  //     }
  //   } catch (e) {
  //     console.log(e);
  //     setLoading(false);
  //   }
  // };

  return (
    <Spin spinning={loading}>
      <div className="loan-header-container">
        <Row>
          <Col xs={24} lg={18}>


            <div className="lh-main-card card-section mr10 mb10" ref={leftCardRef}>

              {/* Header Row: ID, Status, Amount */}
              <Row justify="space-between" align="top" >
                <Col>
                  <div className="lh-app-header">
                    <span className="lh-app-id">
                      <Text className="lh-app-text">{applicantDetails && applicantDetails.ApplicationId}</Text>
                    </span>

                    <StatusTag statusId={applicantDetails.Application_Status_Id} />
                  </div>
                  <div className="lh-date">
                    {applicantDetails && moment(applicantDetails.createdDate).format("MM/DD/YYYY")}
                  </div>
                </Col>
                {/* {isSeniorPrivateBanker && applicantDetails?.IsCBRMismatch === true && (
                  <Col>
                    <span className="cbr-mismatch-warning">
                      CBR Mismatch – Do Not Proceed/Checked
                    </span>
                  </Col>
                )} */}
                {/* {(isSeniorPrivateBanker || isLoanOfficer) && applicantDetails?.IsCBRMismatch === true && (
                  <Col>
                    <span className="cbr-mismatch-warning">
                      CBR Mismatch – Do Not Proceed/Checked
                    </span>
                  </Col>
                )} */}
                {/* { applicantDetails?.IsCBRMismatch === true && (
                  <Col>
                    <span className="cbr-mismatch-warning">
                      CBR Mismatch – Do Not Proceed/Checked
                    </span>
                  </Col>
                )} */}
                <Col>
                  <div className="lh-amount-label">Loan Inquiry Amount</div>
                  <div className="lh-amount-value">
                    ${applicantDetails
                      ? _commonService.formatNumberWithCommas(applicantDetails.App_Det_LoanAmount_BankerAdjusted)
                      : "222,222.00"}
                  </div>
                </Col>
              </Row>



              <Divider />
              {/* Section Title */}
              <div className="lh-section-title">Applicant Information</div>
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={12} md={6}>
                  <DetailItem label="First Name" value={applicantDetails?.App_Det_FirstName || "N/A"} />
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <DetailItem label="Middle Initial" value={applicantDetails?.App_Det_MiddleInitial || "N/A"} />
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <DetailItem label="Last Name" value={applicantDetails?.App_Det_LastName || "N/A"} />
                </Col>

                <Col xs={24} sm={12} md={6}>
                  <DetailItem label="Application Type" value={applicantDetails?.App_Det_Application_Type || "N/A"} />
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <DetailItem label="Date Of Birth" value={applicantDetails && moment(applicantDetails.App_Det_DateOfBirth).format("MM/DD/YYYY") || "N/A"} />
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <DetailItem label="Phone" value={applicantDetails && applicantDetails.App_Det_ContactNumber ? _commonService.formatUSPhoneNumber(applicantDetails.App_Det_ContactNumber) : "N/A"} />
                </Col>


                <Col xs={24} sm={12} md={6}>
                  <DetailItem label="City" value={applicantDetails && applicantDetails.Ident_City || "N/A"} />
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <DetailItem label="Email" value={applicantDetails && applicantDetails.Email || "N/A"} />
                </Col>
              </Row>

              {applicantDetails?.sponserModel?.listsponserFundModel?.length > 0 && (
                <>
                  <Divider />
                  <div className="lh-section-title">Fund Details</div>
                  <Table
                    dataSource={applicantDetails.sponserModel.listsponserFundModel}
                    rowKey="Id"
                    pagination={false}
                    size="small"
                    className="transparent-table"
                    columns={[
                      { title: "Fund Name", dataIndex: "FundName", key: "FundName" },
                      {
                        title: "Fund Value",
                        dataIndex: "FundValue",
                        key: "FundValue",
                        render: (val) => val ? `$${_commonService.formatNumberWithCommas(val)}` : "N/A",
                      },
                      { title: "Vintage Year", dataIndex: "VintageYear", key: "VintageYear" },
                      { title: "Asset Class", dataIndex: "AssetClassType", key: "AssetClassType" },
                    ]}
                  />
                </>
              )}
            </div>
          </Col>
          {/* ================= RIGHT PANEL ================= */}
          <Col xs={24} lg={6}>
            <div className="lh-right-column card-section">

              {/* Box 1: Limit Usage (Updated for perfect visual match) */}
              <div className="lh-sidebar-box">
                <div className="lh-section-title" >Limit Usage</div>

                {/* Container to center the chart vertically and horizontally */}
                <div className="lh-chart-center-wrapper">
                  {/* {JSON.stringify(applicantDetails?.sponserModel)}
                {JSON.stringify(Number(applicantDetails.sponserModel.Utilized))} */}
                  <Progress
                    type="circle"
                    percent={applicantDetails?.sponserModel ? Number(applicantDetails.sponserModel.Utilized) : 0}

                    /* KEY CHANGE: Thicker stroke to match image */
                    strokeWidth={12}
                    strokeColor="#42736f"

                    /* KEY CHANGE: Darker trail for better contrast */
                    trailColor="#262626"

                    width={140}
                    format={(percent) => (
                      <div className="lh-chart-text">
                        <div className="lh-chart-percent">{percent}%</div>
                        <div className="lh-chart-sub">Utilized</div>
                      </div>
                    )}
                  />
                </div>
              </div>

              {/* Box 2: Pre-Qualify */}
              {(applicantDetails?.IsPreQualify === true) && (
                <div className="lh-sidebar-box lh-prequalify-box">
                  <div className="lh-prequalify-header">
                    <CheckCircleOutlined /> PRE-QUALIFY
                  </div>
                  <div className="lh-prequalify-text">
                    This application meets the underwriting criteria for available capital providers.
                  </div>
                </div>
              )}

            </div>

            {showReviewComments && (
              <>
                {/* <div className="lh-right-column card-section mt10">
                <div className="lh-sidebar-box">
                  <span className="cbr-mismatch-warning">
                    CBR Mismatch – Do Not Proceed/Checked
                  </span>
                </div>
              </div> */}
                <div className="lh-right-column card-section mt10 mb10">
                  {/* {hasCbrMismatch && (
                    <>
                      <span className="cbr-mismatch-warning">
                        CBR Mismatch – Do Not Proceed/Checked
                      </span>
                      <Divider style={{ margin: "0 -24px 16px -24px", width: "calc(100% + 48px)", minWidth: "calc(100% + 48px)" }} />
                    </>
                  )} */}

                  
                  <div
                    className="lh-sidebar-box lh-comments-scroll"
                    ref={reviewBoxRef}
                    style={{
                      overflowY: "auto",
                      overflowX: "hidden",
                      paddingRight: 6,
                      marginRight: -6,
                      ...(reviewBoxMaxHeight ? { maxHeight: reviewBoxMaxHeight } : {}),
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        marginBottom: 20,
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: "10px" }}>
                        <BorderOutlined style={{ color: "rgba(244,244,245,0.6)", fontSize: 14 }} />
                        <Text style={{ color: "rgba(244,244,245,0.85)", fontSize: 14 }}>
                          Review Comments
                        </Text>
                      </div>
                      <Text style={{ color: "rgba(244,244,245,0.45)", fontSize: 12 }}>
                        {(applicantDetails?.listCbrMisMatchComments || []).length} updates
                      </Text>
                    </div>
                    <Divider style={{ margin: "0 -24px 16px -24px", width: "calc(100% + 48px)", minWidth: "calc(100% + 48px)" }} />

                    {(applicantDetails?.listCbrMisMatchComments || []).map((item, index) => {
                      const commenterName = item.ReviewCommentsBankerName || item.ReviewCommentsLoanOfficerName || "N/A";
                      const isLast = index === (applicantDetails?.listCbrMisMatchComments || []).length - 1;
                      const roleTagStyle = getRoleTagStyle(item.RoleName);
                      // Only flag the Compliance Administrator's comment when there's an actual CBR
                      // mismatch — the review chain itself always shows, the flag is conditional.
                      const isComplianceAdminEntry = hasCbrMismatch && item.RoleName === "Compliance Administrator";

                      return (
                        <div key={item.Id || index}>
                          <div
                            style={{
                              display: "flex",
                              ...(isComplianceAdminEntry
                                ? {
                                    background: "rgba(239, 68, 68, 0.08)",
                                    borderLeft: "3px solid #EF4444",
                                    borderRadius: 4,
                                    padding: "8px 8px 8px 10px",
                                    marginLeft: -10,
                                  }
                                : {}),
                            }}
                          >
                            <div style={{ marginRight: 12 }}>
                              <div
                                style={{
                                  width: 32,
                                  height: 32,
                                  borderRadius: "50%",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  fontSize: 12,
                                  fontWeight: 600,
                                  color: "#fff",
                                  backgroundColor: getAvatarColor(commenterName),
                                  flexShrink: 0,
                                }}
                              >
                                {getInitials(commenterName)}
                              </div>
                            </div>

                            <div style={{ paddingBottom: 16, flex: 1, minWidth: 0 }}>
                              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 6 }}>
                                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                                  {isComplianceAdminEntry && (
                                    <span role="img" aria-label="CBR mismatch warning" title="CBR Mismatch – Do Not Proceed/Checked" style={{ fontSize: 14 }}>
                                      🚩
                                    </span>
                                  )}
                                  <Text strong style={{ color: "rgba(244,244,245,0.9)" }}>
                                    {commenterName}
                                  </Text>
                                  <span
                                    style={{
                                      background: roleTagStyle.bg,
                                      color: roleTagStyle.color,
                                      borderRadius: 4,
                                      padding: "1px 8px",
                                      fontSize: 12,
                                    }}
                                  >
                                    {getRoleDisplayName(item.RoleName) || "N/A"}
                                  </span>
                                </div>
                                <Text style={{ color: "rgba(244,244,245,0.45)", fontSize: 12, whiteSpace: "nowrap" }}>
                                  {item.ReviewCommentDate ? moment(item.ReviewCommentDate).format("MMM D, h:mm A") : ""}
                                </Text>
                              </div>

                              <div style={{ color: "rgba(244,244,245,0.75)", fontSize: 13, marginTop: 6, marginBottom: 8 }}>
                                {item.Comments || "N/A"}
                              </div>

                              {item.ApplicationStaus && (
                                <span
                                  style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 6,
                                    border: "1px solid rgba(244,244,245,0.15)",
                                    borderRadius: 6,
                                    padding: "2px 8px",
                                    fontSize: 12,
                                    color: "rgba(244,244,245,0.7)",
                                  }}
                                >
                                  <BorderOutlined style={{ fontSize: 12 }} />
                                  {item.ApplicationStaus}
                                </span>
                              )}
                            </div>
                          </div>
                          {!isLast && <Divider style={{ margin: "12px 0" }} />}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </>
            )}

            {/* {showReviewComments && (
             
            )} */}

            {/* Separate card: Limit Usage (duplicate section) */}

          </Col>



          {/* <Col
            className="gutter-row"
            xs={{ span: 12 }}        
            sm={{ span: 12 }}
            md={{ span: 12 }}
            lg={{ span: 12 }}
            xl={{ span: 6}} 
            xxl={{ span: 6 }} 
          >

            <Row className="iconFont" align="middle" style={{ marginBottom: 16 }}>
              <Text strong className="ant-card-head-title" style={{ fontSize: 16 }}>Limit Usage</Text>
              <Tooltip title="What is Limit Usage?">
                <QuestionCircleOutlined className="ml10" />
              </Tooltip>
            </Row>
            <DonutChart data={[
              { type: 'Remaining', value: applicantDetails && applicantDetails.sponserModel && Number(applicantDetails.sponserModel.Remaining) || 0 },
              { type: 'Utilized', value: applicantDetails && applicantDetails.sponserModel && Number(applicantDetails.sponserModel.Utilized) || 0 },
            ]}
              title="Budget"
            />
          </Col> */}


        </Row>
      </div>
    </Spin >
  );
};

export default LoanDetailsHeader;